import { useMemo } from 'react'
import {
  getRegistrationPeriodStatus,
  RegistrationPeriodStatus,
} from '../utils/registrationPeriod'

/**
 * 🔄 useRegistrationPeriod: React hook for determining registration period status
 *
 * This hook provides a convenient way to check if a registration is currently open,
 * closed, or not yet started. It automatically handles timezone conversions and
 * ensures that registrations with the same start and end date are considered
 * open for the entire day in the user's local timezone.
 *
 * @param startsAt - ISO-8601 date string (UTC) when registration starts
 * @param endsAt - ISO-8601 date string (UTC) when registration ends
 * @returns RegistrationPeriodStatus object with status and helper flags
 *
 * @example
 * ```typescript
 * // In a React component
 * const MyComponent = ({ registration }) => {
 *   const {
 *     status,
 *     isBeforeStart,
 *     isAfterEnd,
 *     message
 *   } = useRegistrationPeriod(
 *     registration?.starts_at,
 *     registration?.ends_at
 *   )
 *
 *   if (status === 'before') {
 *     return <div>Registration not yet open</div>
 *   }
 *
 *   if (status === 'after') {
 *     return <div>Registration is closed</div>
 *   }
 *
 *   return <div>Registration is open!</div>
 * }
 * ```
 *
 * @example
 * ```typescript
 * // With conditional rendering
 * const RegistrationForm = ({ registration }) => {
 *   const { status, message } = useRegistrationPeriod(
 *     registration?.starts_at,
 *     registration?.ends_at
 *   )
 *
 *   return (
 *     <div>
 *       {status === 'active' && <RegistrationFormContent />}
 *       {status !== 'active' && <div>{message}</div>}
 *     </div>
 *   )
 * }
 * ```
 */
export const useRegistrationPeriod = (
  startsAt?: string | null,
  endsAt?: string | null
): RegistrationPeriodStatus => {
  console.log('🔄 useRegistrationPeriod: Hook called with dates', {
    startsAt,
    endsAt,
  })

  return useMemo(() => {
    const result = getRegistrationPeriodStatus(startsAt, endsAt)
    console.log('🔄 useRegistrationPeriod: Computed result', result)
    return result
  }, [startsAt, endsAt])
}
